import { useCallback, useEffect, useMemo, useState } from 'react';
import { get, put, remove } from '../../../../../redux/api';
import { useAddToast } from '../../../../../contexts/ToastContext';
import type { OrganizationDomain } from '../../../../../../shared/model-typings';
import type { LDAPStrategy } from '../../../../../types';
import { useAsyncRequest } from '../../../../../hooks';
import { OrganizationSsoMethod } from '../../../../../../typings/enums';

/**
 * Fetches the domains for the organization. Also provides methods for modifying the domains.
 */
function useOrganizationDomainsAsyncRequest(organizationId: number) {
  const addToast = useAddToast();
  const [domains, setDomains] = useState<OrganizationDomain[] | null>(null);
  const request = useCallback(() => {
    return get<OrganizationDomain[]>(`/api/organizations/${organizationId}/domains`);
  }, [organizationId]);
  const asyncValue = useAsyncRequest(request);

  useEffect(() => {
    if (asyncValue.value) {
      setDomains(asyncValue.value);
    }
  }, [asyncValue.value]);

  useEffect(() => {
    if (asyncValue.errors) {
      addToast({ type: 'error', content: `Failed to fetch domains.` });
    }
  }, [addToast, asyncValue.errors]);

  return { domains, setDomains, ...asyncValue };
}

/**
 * Fetches the domains for the organization.
 */
function useOrganizationDomains(organizationId: number) {
  const addToast = useAddToast();
  const { domains, setDomains, ...value } = useOrganizationDomainsAsyncRequest(organizationId);

  const hasSamlDomain = useMemo(
    () => domains?.some(({ ssoMethod }) => ssoMethod === OrganizationSsoMethod.SAML),
    [domains],
  );

  const onDeleteDomain = async (domain: string) => {
    try {
      await remove(`/api/organizations/${organizationId}/domains/${domain}`);
      setDomains((prevDomains) => prevDomains?.filter((d) => d.domain !== domain) || null);
    } catch (error: unknown) {
      addToast({ type: 'error', content: `Failed to delete domain.` });
    }
  };

  const onSaveLDAP = async (ldap: Partial<LDAPStrategy>): Promise<{ data: Partial<LDAPStrategy> }> => {
    try {
      const response = await put('/api/ldap', ldap);
      return response as { data: Partial<LDAPStrategy> };
    } catch (error: unknown) {
      addToast({ type: 'error', content: `Failed to update LDAP configuration.` });
      throw error;
    }
  };

  return {
    domains,
    isLoading: value.isLoading,
    errors: value.errors,
    onDeleteDomain,
    onSaveLDAP,
    hasSamlDomain,
  };
}

export default useOrganizationDomains;
